import { Button, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  useBankAccountTemplatesQuery,
  useDeleteBankAccountTemplate,
} from "../api/queries";
import { BankAccountTemplateTable } from "../ui/BankAccountTemplateTable";

export function BankAccountTemplatesPage() {
  const nav = useNavigate({ from: "/app/performance/bank-account-templates" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/performance/bank-account-templates",
  });
  const list = useBankAccountTemplatesQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });
  const canCreate = useActionPermission(["performance-bank-account:create"]);
  const deleteTemplate = useDeleteBankAccountTemplate();

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ແມ່ແບບບັນຊີທະນາຄານ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">
              ແມ່ແບບບັນຊີທະນາຄານ
            </h2>
            <p className="text-muted-foreground">
              ຕັ້ງເນື້ອຫາແຍກສຳລັບເອກະສານມີພາສີ / ບໍ່ມີພາສີ
            </p>
          </div>
          {canCreate && (
            <Button
              onClick={() =>
                nav({ to: "/app/performance/bank-account-templates/create" })
              }
            >
              <PlusIcon className="size-4" /> ເພີ່ມແມ່ແບບ
            </Button>
          )}
        </div>

        <div className="rounded-xl border bg-card pt-2">
          <BankAccountTemplateTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onEdit={(item) =>
              nav({
                to: "/app/performance/bank-account-templates/$id/edit",
                params: { id: item.id },
              })
            }
            onDelete={async (id) => {
              try {
                await deleteTemplate.run(id);
                toast.success("ລຶບແມ່ແບບສຳເລັດ");
              } catch {}
            }}
          />
        </div>
      </Main>
    </>
  );
}
